import { Clock, Loader2 } from "lucide-react";
import { Button } from "@/frontend/components/ui/button";
import { ACCEPT_ERROR, CONSENT_DURATION } from "@/frontend/constants/consent";
import type { RequesterType } from "@/server/constants/persona";
import { ConsentDisclosure, ConsentTermsLine } from "./ConsentDisclosure";
import { ConsentPageFrame } from "./ConsentPageFrame";

type ConsentPageProps = {
  /** The organization that invited the patient. The copy is neutral when this is unknown. */
  organizationName?: string | null;
  /**
   * The kind of requester, from `organization.requester_type`. The copy does
   * not name the kind when this value is null or absent.
   */
  requesterType?: RequesterType | null;
  /**
   * True for the individual workflow. In that workflow the reader is the
   * patient, the records are their own, and no other party asks for them.
   */
  ownRecords?: boolean;
  /** Records the acceptance, then sends the patient to the portal picker. */
  onAgree: () => void;
  /** True while the acceptance write is in progress. */
  isSubmitting?: boolean;
  /** True when the last acceptance attempt failed. It shows the retry line. */
  hasError?: boolean;
  /** Opens the exit confirmation. */
  onExit: () => void;
};

/**
 * The full patient consent, on one page. It names the party that asks for the
 * records and the kind of party they are. It then tells the patient how we hold
 * the records, what happens next, and what Medicare requires us to say.
 * The button is the agreement, and the text beside it says so. Thus the page
 * has no checkbox.
 */
export function ConsentPage({
  organizationName,
  requesterType,
  ownRecords = false,
  onAgree,
  isSubmitting = false,
  hasError = false,
  onExit,
}: ConsentPageProps) {
  return (
    <ConsentPageFrame
      onExit={onExit}
      eyebrow={
        <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-meta font-medium text-muted-foreground">
          <Clock className="size-3" />
          {CONSENT_DURATION}
        </span>
      }
      terms={<ConsentTermsLine />}
      footer={
        <div className="flex flex-col gap-2">
          {hasError ? (
            <p role="alert" className="text-caption text-destructive">
              {ACCEPT_ERROR}
            </p>
          ) : null}
          <Button className="h-11 w-full text-body" disabled={isSubmitting} onClick={onAgree}>
            {isSubmitting ? <Loader2 className="size-4 animate-spin" /> : null}
            Agree and continue
          </Button>
        </div>
      }
    >
      <div className="flex flex-col">
        <h1 className="text-lg font-semibold tracking-tight">
          Let&rsquo;s get your records connected
        </h1>

        <ConsentDisclosure
          organizationName={organizationName}
          requesterType={requesterType}
          ownRecords={ownRecords}
        />
      </div>
    </ConsentPageFrame>
  );
}
